import { scaleBand, scaleLinear, max } from 'd3';
import * as d3 from 'd3';
import numberWithCommas from '../../utils/numberWithCommas';

const width = 360;
const height = 226;

const margin = { top: 0, right: 55, bottom: 0, left: 90 };

// To be reformatted once real data is in
const yAxisTickFormat = (tickValue: string) => { return tickValue.replace("United States", "USA"); };
const hoverColor = "#eec42d";
const staticColor = '#437c90';

interface BarChartAudienceLocationProps {
	data: Array<any>;
	loading?: boolean;
	error?: string | null;
}
interface AxisLeftProps {
	yScale: any;
	tickFormat: any;
}

interface MarksProps {
	data: any;
	xScale: any;
	yScale: any;
	xValue: any;
	yValue: any;
	innerWidth: number;
	audienceSize: number;
}

// Creates the left axxis of the bar chart
const AxisLeft = ({ yScale, tickFormat }: AxisLeftProps) =>
	yScale.domain().map((tickValue: number) => (
		<g
			className="barchart-tick"
			key={tickValue} >
			<text
				x={-6}
				y={yScale(tickValue) +
					yScale.bandwidth() / 2}
				style={{ textAnchor: 'end' }}
			>
				{tickFormat(tickValue)}
			</text>
		</g>

	));

// Creates the bars that will display on the chart
const Marks = ({ data, xScale, yScale, xValue, yValue, innerWidth, audienceSize }: MarksProps) => {

	const tooltip = d3
		.select('.d3-tooltip-location')
		.style('position', 'absolute')
		.style('z-index', '10')
		.style('visibility', 'hidden')
		.style('padding', '10px')
		.style('background', 'rgba(0,0,0,0.6)')
		.style('border-radius', '4px')
		.style('color', '#fff')
		.text('a simple tooltip');

	// Cycles through all the data elements that are provided and creates two rectangles, 
	// One background and one for the foreground 
	return (data.map((d: any) => (
		<g key={`${yValue(d)}`}>
			<rect
				className={"barchart-mark-background"}
				x={0}
				y={yScale(yValue(d))}
				width={innerWidth}
				height={yScale.bandwidth()}
				rx={4}
				fill={"blue"}
			/>

			<rect
				className={"barchart-mark"}
				x={0}
				y={yScale(yValue(d))}
				width={xScale(xValue(d))}
				height={yScale.bandwidth()}
				rx={4}
				fill={"blue"}
				onMouseEnter={function() {
					tooltip
						.html(`<div>${numberWithCommas(xValue(d))}</div>`)
						.style('visibility', 'visible');
					d3.select(".d3-tooltip-retention").transition().attr('fill', hoverColor);
				}}

				// Enables the tooltop
				onMouseMove={function(event: any) {
					tooltip
						.style('top', event.pageY - 10 + 'px')
						.style('left', event.pageX + 10 + 'px');
				}}

				// Hides the tooltip
				onMouseOut={function() {
					tooltip.html(``).style('visibility', 'hidden');
					d3.select(".d3-tooltip-retention").transition().attr('fill', staticColor);
				}}
			>
			</rect>
			<g className={"barchart-mark-percentage"}>
				<text
					x={innerWidth + 6}
					y={yScale(yValue(d)) +
						yScale.bandwidth() / 2}
					style={{ textAnchor: 'start' }}
				>
					{/* This value will be replaced with the actual total number that is used */}
					{(xValue(d) / audienceSize * 100).toFixed(1).toString() + "%"}
				</text>
			</g>
		</g>
	)));
};


function BarChartAudienceLocation({ data, loading, error }: BarChartAudienceLocationProps): JSX.Element {
	// Display nothing because the map component will display it
	if (error || loading || !data?.length) {
		return <div></div>;
	}

	const yValue = (d: any) => d.country;
	const xValue = (d: any) => d.count;
	const innerHeight = height - margin.top - margin.bottom;
	const innerWidth = width - margin.left - margin.right;
	const audienceSize = data.reduce((sum, value) => sum + value.count, 0);

	// Creates a linearly interpolated scale for the y axis
	const yScale = scaleBand()
		.domain(data.map(yValue))
		.range([0, innerHeight])
		.paddingInner(0.4);

	// Creates a linearly interpolated scale for the x axis
	const xScale = scaleLinear()
		.domain([0, max(data, xValue)])
		.range([0, innerWidth]);

	return (
		// Assembles the chart's pieces together
		<>
			<svg className="barChart-container" width={width} height={height}>
				<g key={"barChart-container"} transform={`translate(${margin.left},${margin.top})`}>
					<AxisLeft
						yScale={yScale}
						tickFormat={yAxisTickFormat}
					/>
					<Marks
						data={data}
						xScale={xScale}
						yScale={yScale}
						xValue={xValue}
						yValue={yValue}
						innerWidth={innerWidth}
						audienceSize={audienceSize}
					/>
				</g>
			</svg>

			<div className="d3-tooltip-location"></div>
		</>
	);
}

export default BarChartAudienceLocation;
